import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, MapPin } from "lucide-react";
import { CLINIC } from "../lib/api";
import TrustBar from "../components/TrustBar";
import Differentiators from "../components/Differentiators";
import TestimonialsSection from "../components/TestimonialsSection";
import CTASection from "../components/CTASection";

export default function DentistaCalafell() {
  return (
    <div data-testid="dentista-calafell-page">
      <section className="py-20 bg-gradient-to-b from-teal-50/50 to-white">
        <div className="container-max">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <MapPin className="w-3.5 h-3.5 text-teal-700" />
              <span className="text-xs font-semibold text-slate-700">Calafell Platja · Tarragona</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Dentista en Calafell con <span className="underline-accent">40+ años</span> de confianza
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl">
              Clínica Dental Pina es la clínica dental de referencia en Calafell Platja desde 1984.
              Somos una clínica familiar, no una franquicia: aquí te atiende siempre el mismo doctor,
              con diagnósticos honestos y tratamientos de máxima calidad. Residentes, familias,
              segundas residencias y turistas: todos encuentran en nosotros la atención que merecen.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contacto" className="btn-primary" data-testid="dentista-cta-1">
                Primera visita gratis <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${CLINIC.phone}`} className="btn-secondary">
                <Phone className="w-4 h-4" /> {CLINIC.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      <section className="py-24 bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                ¿Por qué somos el <span className="underline-accent">dentista de referencia</span> en Calafell?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Una trayectoria de más de cuatro décadas nos avala. Hemos visto crecer a generaciones
                enteras de familias de Calafell, Cunit, El Vendrell y toda la costa dorada.
              </p>
              <ul className="space-y-4">
                {[
                  "Ubicación privilegiada junto a la playa y estación Renfe",
                  "Equipo multidisciplinar para atención integral",
                  "Tecnología de vanguardia: TAC 3D, escáner intraoral digital",
                  "Atención en español, catalán, inglés y francés",
                  "Especialistas en pacientes con miedo al dentista",
                  "Primera visita gratuita con diagnóstico completo",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_clinica-calafell-v2/artifacts/yeaa66bv_20210226_144429.jpg"
                alt="Clínica Dental en Calafell"
                className="w-full h-[520px] object-cover rounded-3xl"
              />
            </div>
          </div>
        </div>
      </section>

      <Differentiators />

      <section className="py-24 bg-slate-50">
        <div className="container-max">
          <div className="max-w-3xl mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              Tratamientos dentales que ofrecemos en Calafell
            </h2>
            <p className="text-lg text-slate-600">
              Odontología integral en un solo lugar. Sin desplazamientos, sin derivaciones.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/implantes-dentales-calafell", title: "Implantes dentales", desc: "Recupera piezas perdidas" },
              { to: "/ortodoncia-invisible-calafell", title: "Ortodoncia invisible", desc: "Invisalign certificado" },
              { to: "/estetica-dental", title: "Estética dental", desc: "Carillas y blanqueamiento" },
              { to: "/odontopediatria", title: "Odontopediatría", desc: "Dentistas para niños" },
              { to: "/urgencias-dentales-calafell", title: "Urgencias dentales", desc: "Atención mismo día" },
              { to: "/contacto", title: "Revisión general", desc: "Primera visita gratuita" },
            ].map((s, i) => (
              <Link
                key={i}
                to={s.to}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-lg transition-all group"
                data-testid={`dentista-service-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">{s.title}</h3>
                    <p className="text-sm text-slate-600">{s.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-700 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <CTASection />
    </div>
  );
}
